import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { displayProduct } from "../../lib/products";
import { toast } from "sonner";
import { PageHeader, SectionPad, Badge } from "../../components/ui-extras";
import { Plus, Trash, PencilSimple, Lightning, ArrowRight } from "@phosphor-icons/react";
import BackdatedConfirmModal, { isBackdated } from "../../components/BackdatedConfirmModal";

// Fixed product set per the product spec (mirrors backend PRICING_GROUP_PRODUCTS).
// DEFD removed Feb 2026 — DEF margin is now per-customer on the profile.
const PRODUCTS = {
  esso: ["DIESEL_LS", "DSL_EFF_LS", "RFR"],
  // Pipeline gained DIESEL_DYED on 2026-02-12 to represent the supplier's
  // "Diesel Co" (mirrors backend PRICING_GROUP_PRODUCTS).
  pipeline: ["DIESEL_LS", "DIESEL_DYED"],
};
const NETWORKS = ["esso", "pipeline"];

// Phase 3E-9e — Petro-Pass country-strict lanes. Legacy ESSO/Pipeline
// slots use the flat `"{network}:{product}"` key shape. Petro-Pass
// slots use the extended `"petro_pass:{CA|US}:{product}"` key shape
// so PP-CA (CAD/L, ¢/L) and PP-US (USD/GAL, ¢/GAL) never collide.
const PETRO_PASS_LANES = [
  { country: "CA", product: "DIESEL_LS", label: "Petro-Pass Canada · CAD/L",  unit: "¢/L"   },
  { country: "US", product: "DIESEL_LS", label: "Petro-Pass USA · USD/GAL", unit: "¢/GAL" },
];
const _ppKey = (cc, prod) => `petro_pass:${cc}:${prod}`;
const _isDefinedSlot = (v) => v !== "" && v !== null && v !== undefined;
const _totalSlots = () =>
  Object.values(PRODUCTS).reduce((a, xs) => a + xs.length, 0)
  + PETRO_PASS_LANES.length;

const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = () => ({ name: "", description: "", customer_ids: [], margins: {}, default_effective_date: today() });

export default function PricingGroups() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  // null = list view; "new" or { ...group } = edit view (single-page state, kept simple)
  const [editing, setEditing] = useState(null);
  const [applyingFor, setApplyingFor] = useState(null);  // group obj when prompting effective date
  const [applyForm, setApplyForm] = useState({ effective_date: today(), mode: "overwrite" });
  // Phase 3E-9Q — backdated-apply confirmation. Populated when a caller
  // (applyGroup or bumpDate+apply) detects effective_date < today. The
  // stored `retry` closure re-runs the exact API call with
  // ?confirm_backdated=true after the operator explicitly acks.
  const [backdated, setBackdated] = useState(null);  // { open, effectiveDate, contextLabel, customerCount, ruleCount, retry, busy }
  const [busy, setBusy] = useState(false);
  const [customerFilter, setCustomerFilter] = useState("");

  const load = async () => {
    const [g, c] = await Promise.all([api.get("/pricing-groups"), api.get("/customers")]);
    setRows(g.data); setCustomers(c.data);
  };
  useEffect(() => { load(); }, []);

  const customerNameById = useMemo(() => {
    const m = {};
    customers.forEach((c) => { m[c.customer_id] = c.company_name; });
    return m;
  }, [customers]);

  const startNew = () => setEditing({ ...emptyForm(), _isNew: true });
  const startEdit = (g) => setEditing({
    ...g,
    customer_ids: [...(g.customer_ids || [])],
    margins: { ...(g.margins || {}) },
  });
  const cancel = () => { setEditing(null); setCustomerFilter(""); };

  const saveGroup = async () => {
    const payload = {
      name: editing.name?.trim(),
      description: editing.description || "",
      default_effective_date: editing.default_effective_date || today(),
      customer_ids: editing.customer_ids || [],
      // Coerce empty strings to nothing so the backend treats them as "unset".
      margins: Object.fromEntries(
        Object.entries(editing.margins || {}).filter(([, v]) => v !== "" && v !== null && v !== undefined)
      ),
    };
    if (!payload.name) return toast.error("Group name is required");
    setBusy(true);
    try {
      if (editing._isNew) {
        await api.post("/pricing-groups", payload);
        toast.success("Group created");
      } else {
        await api.put(`/pricing-groups/${editing.group_id}`, payload);
        toast.success("Group updated");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const removeGroup = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"? Customer-level margin rules already created from this group will NOT be removed.`)) return;
    try { await api.delete(`/pricing-groups/${g.group_id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Phase 3E-9Q — small helper: count non-blank margin slots for a group so
  // the backdated modal can show "N margin definitions".
  const _slotCount = (group) => Object.values(group?.margins || {})
    .filter((v) => v !== "" && v !== null && v !== undefined).length;

  // Bump the group's saved effective date forward by one day so the next Apply
  // stamps tomorrow's rules. Margins matrix and member list are untouched —
  // user can tweak cells via Edit before applying.
  const bumpDate = async (g, alsoApply = false) => {
    const cur = g.default_effective_date || today();
    const next = new Date(cur);
    next.setDate(next.getDate() + 1);
    const newDate = next.toISOString().slice(0, 10);
    // Inner function so we can retry with ?confirm_backdated=true on ack.
    const doIt = async (extraQuery = "") => {
      await api.put(`/pricing-groups/${g.group_id}`, {
        name: g.name,
        description: g.description || "",
        default_effective_date: newDate,
        customer_ids: g.customer_ids || [],
        margins: g.margins || {},
      });
      if (!alsoApply) {
        toast.success(`"${g.name}" → effective ${newDate}`);
        load();
        return;
      }
      const { data } = await api.post(
        `/pricing-groups/${g.group_id}/apply${extraQuery}`,
        { effective_date: newDate, mode: "overwrite" },
      );
      toast.success(
        `"${g.name}" bumped to ${newDate} & applied · `
        + `${data.customers} customer${data.customers === 1 ? "" : "s"} · `
        + `${data.inserted} inserted · ${data.updated} updated · ${data.skipped} skipped`
      );
      load();
    };
    try {
      // Preflight: if bumping to the future, backend won't gate — just run.
      // If (unlikely) alsoApply is true and newDate is still < today, gate.
      if (alsoApply && isBackdated(newDate)) {
        setBackdated({
          open: true,
          effectiveDate: newDate,
          contextLabel: `Bump + apply pricing group "${g.name}"`,
          customerCount: (g.customer_ids || []).length,
          ruleCount: _slotCount(g),
          retry: async () => {
            setBackdated((b) => b && { ...b, busy: true });
            try {
              await doIt("?confirm_backdated=true");
              setBackdated(null);
            } catch (e) {
              toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Bump+Apply failed");
              setBackdated((b) => b && { ...b, busy: false });
            }
          },
        });
        return;
      }
      await doIt();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "BACKDATED_MARGIN_CONFIRMATION_REQUIRED") {
        setBackdated({
          open: true,
          effectiveDate: d.effective_date,
          contextLabel: `Bump + apply pricing group "${g.name}"`,
          customerCount: (g.customer_ids || []).length,
          ruleCount: _slotCount(g),
          retry: async () => {
            setBackdated((b) => b && { ...b, busy: true });
            try {
              await doIt("?confirm_backdated=true");
              setBackdated(null);
            } catch (err) {
              toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Bump+Apply failed");
              setBackdated((b) => b && { ...b, busy: false });
            }
          },
        });
        return;
      }
      toast.error(d?.message || d || (alsoApply ? "Bump+Apply failed" : "Bump failed"));
    }
  };

  const applyGroup = async () => {
    if (!applyForm.effective_date) return toast.error("Pick an effective date");
    const g = applyingFor;
    const doApply = async (extraQuery = "") => {
      const { data } = await api.post(
        `/pricing-groups/${g.group_id}/apply${extraQuery}`,
        applyForm,
      );
      toast.success(
        `Applied to ${data.customers} customer${data.customers === 1 ? "" : "s"} · `
        + `${data.inserted} inserted · ${data.updated} updated · ${data.skipped} skipped`
      );
      setApplyingFor(null);
      load();
    };
    setBusy(true);
    try {
      // Preflight — surface the modal BEFORE hitting the backend when we
      // can already tell the date is in the past. Faster feedback + one
      // less round-trip. Backend still enforces on the retry.
      if (isBackdated(applyForm.effective_date)) {
        setBackdated({
          open: true,
          effectiveDate: applyForm.effective_date,
          contextLabel: `Apply pricing group "${g.name}"`,
          customerCount: (g.customer_ids || []).length,
          ruleCount: _slotCount(g),
          retry: async () => {
            setBackdated((b) => b && { ...b, busy: true });
            try {
              await doApply("?confirm_backdated=true");
              setBackdated(null);
            } catch (e) {
              toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Apply failed");
              setBackdated((b) => b && { ...b, busy: false });
            }
          },
        });
        return;
      }
      await doApply();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "BACKDATED_MARGIN_CONFIRMATION_REQUIRED") {
        // Defensive: backend flagged something we didn't. Show the modal.
        setBackdated({
          open: true,
          effectiveDate: d.effective_date,
          contextLabel: `Apply pricing group "${g.name}"`,
          customerCount: (g.customer_ids || []).length,
          ruleCount: _slotCount(g),
          retry: async () => {
            setBackdated((b) => b && { ...b, busy: true });
            try {
              await doApply("?confirm_backdated=true");
              setBackdated(null);
            } catch (err) {
              toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Apply failed");
              setBackdated((b) => b && { ...b, busy: false });
            }
          },
        });
        return;
      }
      toast.error(d?.message || d || "Apply failed");
    } finally { setBusy(false); }
  };

  // ---- LIST VIEW ----
  if (!editing) {
    return (
      <SectionPad>
        <PageHeader
          title="Pricing groups"
          subtitle="DEFAULT MARGINS / BULK APPLY"
          testId="pricing-groups-header"
          actions={
            <button className="gf-btn gf-btn-primary" onClick={startNew} data-testid="new-pricing-group">
              <Plus size={14}/> New group
            </button>
          }
        />

        <div className="gf-card">
          <div className="gf-card-header">
            <h3>Groups <span className="text-xs text-zinc-400">· {rows.length}</span></h3>
            <span className="text-xs text-zinc-500 font-mono">
              Bundled default-scope margins for many customers at once
            </span>
          </div>
          <div className="overflow-auto">
            <table className="gf-table">
              <thead>
                <tr>
                  <th>NAME</th><th>EFF DATE</th><th>CUSTOMERS</th><th>MARGINS DEFINED</th>
                  <th>LAST APPLIED</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((g) => {
                  const definedSlots = Object.entries(g.margins || {})
                    .filter(([, v]) => _isDefinedSlot(v)).length;
                  return (
                    <tr key={g.group_id}>
                      <td>
                        <div className="font-semibold">{g.name}</div>
                        {g.description && <div className="text-xs text-zinc-500">{g.description}</div>}
                      </td>
                      <td className="font-mono text-xs">{g.default_effective_date || <span className="text-zinc-400">—</span>}</td>
                      <td className="font-mono text-xs">{(g.customer_ids || []).length}</td>
                      <td className="font-mono text-xs">{definedSlots} / {_totalSlots()}</td>
                      <td className="font-mono text-xs">
                        {g.last_applied_date
                          ? <>{g.last_applied_date} <Badge variant="success" testId={`group-applied-${g.group_id}`}>OK</Badge></>
                          : <span className="text-zinc-400">never</span>}
                      </td>
                      <td className="text-right">
                        <button
                          className="gf-btn gf-btn-ghost"
                          onClick={() => bumpDate(g)}
                          data-testid={`bump-group-${g.group_id}`}
                          title="Bump effective date by 1 day"
                        >
                          <ArrowRight size={14}/> Bump
                        </button>
                        <button
                          className="gf-btn gf-btn-ghost"
                          onClick={() => bumpDate(g, true)}
                          data-testid={`bump-apply-group-${g.group_id}`}
                          title="Bump date by 1 day AND apply margins to all members"
                        >
                          <Lightning size={14}/> Bump + Apply
                        </button>
                        <button
                          className="gf-btn gf-btn-outline"
                          onClick={() => { setApplyingFor(g); setApplyForm({ effective_date: g.default_effective_date || today(), mode: "overwrite" }); }}
                          data-testid={`apply-group-${g.group_id}`}
                          title="Apply margins to member customers"
                        >
                          <Lightning size={14}/> Apply
                        </button>
                        <button className="gf-btn gf-btn-ghost" onClick={() => startEdit(g)}
                          data-testid={`edit-group-${g.group_id}`}>
                          <PencilSimple size={14}/>
                        </button>
                        <button className="gf-btn gf-btn-ghost" onClick={() => removeGroup(g)}
                          data-testid={`delete-group-${g.group_id}`}>
                          <Trash size={14}/>
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-zinc-400 py-6">No groups yet. Click "New group" to create one.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {applyingFor && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !busy && setApplyingFor(null)}>
            <div className="bg-white w-full max-w-md border border-zinc-200" onClick={(e) => e.stopPropagation()}>
              <div className="gf-card-header"><h3>Apply "{applyingFor.name}"</h3></div>
              <div className="gf-card-body space-y-3">
                <div className="text-xs text-zinc-500">
                  Pushes this group's margins as DEFAULT-scope rules to all{" "}
                  <span className="font-semibold">{(applyingFor.customer_ids || []).length}</span>{" "}
                  member customer{(applyingFor.customer_ids || []).length === 1 ? "" : "s"}.
                  Per-customer city/station overrides are not touched.
                </div>
                <div>
                  <div className="overline mb-1">Effective date</div>
                  <input
                    type="date"
                    className="gf-input"
                    value={applyForm.effective_date}
                    onChange={(e) => setApplyForm({ ...applyForm, effective_date: e.target.value })}
                    data-testid="apply-effective-date"
                  />
                  {isBackdated(applyForm.effective_date) && (
                    <div
                      className="mt-1 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1"
                      data-testid="apply-backdated-inline-warning"
                    >
                      This date is BEFORE today — you'll be asked to confirm before applying.
                    </div>
                  )}
                </div>
                <div>
                  <div className="overline mb-1">If a default rule already exists for this date</div>
                  <select className="gf-input" value={applyForm.mode}
                    onChange={(e) => setApplyForm({ ...applyForm, mode: e.target.value })}
                    data-testid="apply-mode">
                    <option value="overwrite">Overwrite — replace its margin with the group value</option>
                    <option value="skip">Skip — leave existing rule untouched</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
                <button className="gf-btn gf-btn-outline" onClick={() => setApplyingFor(null)} disabled={busy}>Cancel</button>
                <button className="gf-btn gf-btn-primary" onClick={applyGroup} disabled={busy} data-testid="apply-confirm">
                  {busy ? "Applying…" : "Apply"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Phase 3E-9Q — backdated pricing-change guard modal. */}
        <BackdatedConfirmModal
          open={!!backdated?.open}
          onCancel={() => setBackdated(null)}
          onConfirm={() => backdated?.retry && backdated.retry()}
          effectiveDate={backdated?.effectiveDate}
          contextLabel={backdated?.contextLabel}
          customerCount={backdated?.customerCount}
          ruleCount={backdated?.ruleCount}
          busy={!!backdated?.busy}
        />
      </SectionPad>
    );
  }

  // ---- EDIT VIEW ----
  const visibleCustomers = customers.filter((c) =>
    !customerFilter || c.company_name?.toLowerCase().includes(customerFilter.toLowerCase())
  );
  const setMarginCell = (network, product, value) => {
    setEditing({
      ...editing,
      margins: { ...(editing.margins || {}), [`${network}:${product}`]: value },
    });
  };
  const toggleCustomer = (cid) => {
    const cur = new Set(editing.customer_ids || []);
    if (cur.has(cid)) cur.delete(cid); else cur.add(cid);
    setEditing({ ...editing, customer_ids: Array.from(cur) });
  };

  return (
    <SectionPad>
      <PageHeader
        title={editing._isNew ? "New pricing group" : `Edit · ${editing.name || "group"}`}
        subtitle="PRICING GROUP / EDITOR"
        actions={
          <div className="flex gap-2">
            <button className="gf-btn gf-btn-outline" onClick={cancel} disabled={busy}>Cancel</button>
            <button className="gf-btn gf-btn-primary" onClick={saveGroup} disabled={busy} data-testid="save-pricing-group">
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* META */}
        <div className="gf-card">
          <div className="gf-card-header"><h3>Group details</h3></div>
          <div className="gf-card-body space-y-3">
            <div>
              <div className="overline mb-1">Name</div>
              <input className="gf-input" value={editing.name || ""}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="e.g. Group A — 1 cpl"
                data-testid="group-name" />
            </div>
            <div>
              <div className="overline mb-1">Description (optional)</div>
              <input className="gf-input" value={editing.description || ""}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                placeholder="Internal note for this group"
                data-testid="group-description" />
            </div>
            <div>
              <div className="overline mb-1">Effective date</div>
              <input
                type="date"
                className="gf-input"
                value={editing.default_effective_date || today()}
                onChange={(e) => setEditing({ ...editing, default_effective_date: e.target.value })}
                data-testid="group-effective-date"
              />
              <div className="text-xs text-zinc-500 mt-1">
                Date stamped on every margin rule when you click Apply. You can still override it in the Apply dialog.
              </div>
            </div>
          </div>
        </div>

        {/* MARGINS MATRIX */}
        <div className="gf-card">
          <div className="gf-card-header">
            <h3>Margins</h3>
            <span className="text-xs text-zinc-500 font-mono">
              DEF margin lives on customer profile · Canadian lanes are ¢/L · Petro-Pass USA is ¢/GAL
            </span>
          </div>
          <div className="gf-card-body">
            <table className="gf-table">
              <thead>
                <tr>
                  <th>NETWORK / LANE</th>
                  <th>PRODUCT</th>
                  <th className="text-right">MARGIN</th>
                </tr>
              </thead>
              <tbody>
                {NETWORKS.flatMap((net) => PRODUCTS[net].map((prod) => {
                  const key = `${net}:${prod}`;
                  return (
                    <tr key={key}>
                      <td className="font-mono">{net.toUpperCase()} <span className="text-xs text-zinc-500">· CAD/L</span></td>
                      <td className="font-mono text-xs">{displayProduct(prod, net)}</td>
                      <td className="gf-num">
                        <input
                          type="number"
                          step="0.01"
                          className="gf-input"
                          style={{ width: 110, textAlign: "right" }}
                          value={editing.margins?.[key] ?? ""}
                          onChange={(e) => setMarginCell(net, prod, e.target.value)}
                          placeholder="e.g. 1.0"
                          data-testid={`group-margin-${net}-${prod}`}
                        />
                        <span className="text-xs text-zinc-500 ml-1">¢/L</span>
                      </td>
                    </tr>
                  );
                }))}
                {/* Phase 3E-9e — Petro-Pass CA / US country-strict slots.
                    CA is CAD/L (¢/L); US is USD/GAL (¢/GAL). They are
                    stored under EXTENDED keys `petro_pass:CA:DIESEL_LS`
                    and `petro_pass:US:DIESEL_LS` so the two countries
                    never collide on save or on apply. */}
                {PETRO_PASS_LANES.map((lane) => {
                  const key = _ppKey(lane.country, lane.product);
                  return (
                    <tr key={key} data-testid={`group-margin-row-petro-pass-${lane.country.toLowerCase()}`}>
                      <td className="font-mono">{lane.label}</td>
                      <td className="font-mono text-xs">{displayProduct(lane.product, "petro_pass")}</td>
                      <td className="gf-num">
                        <input
                          type="number"
                          step="0.01"
                          className="gf-input"
                          style={{ width: 110, textAlign: "right" }}
                          value={editing.margins?.[key] ?? ""}
                          onChange={(e) => setEditing({
                            ...editing,
                            margins: { ...(editing.margins || {}), [key]: e.target.value },
                          })}
                          placeholder="e.g. 6.0"
                          data-testid={`group-margin-petro-pass-${lane.country.toLowerCase()}-${lane.product}`}
                        />
                        <span className="text-xs text-zinc-500 ml-1">{lane.unit}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="text-xs text-zinc-500 mt-3 font-mono">
              Leave blank to skip that slot. Zero is a valid margin and creates
              an explicit 0¢ rule at apply time. DEF margins moved to the
              customer profile (Preferences → Default DEF margin) — set them
              per-customer.
            </div>
          </div>
        </div>
      </div>

      {/* CUSTOMERS */}
      <div className="gf-card mt-4">
        <div className="gf-card-header">
          <h3>Members <span className="text-xs text-zinc-400">· {(editing.customer_ids || []).length} selected</span></h3>
          <input
            className="gf-input text-xs"
            style={{ maxWidth: 240 }}
            placeholder="Filter customers…"
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            data-testid="group-customer-filter"
          />
        </div>
        <div className="overflow-auto" style={{ maxHeight: 360 }}>
          <table className="gf-table">
            <thead style={{ position: "sticky", top: 0, background: "white" }}>
              <tr>
                <th style={{ width: 32 }}>
                  <input
                    type="checkbox"
                    checked={visibleCustomers.length > 0 && visibleCustomers.every((c) => editing.customer_ids?.includes(c.customer_id))}
                    onChange={(e) => {
                      const ids = new Set(editing.customer_ids || []);
                      if (e.target.checked) visibleCustomers.forEach((c) => ids.add(c.customer_id));
                      else visibleCustomers.forEach((c) => ids.delete(c.customer_id));
                      setEditing({ ...editing, customer_ids: Array.from(ids) });
                    }}
                    data-testid="group-customers-select-all"
                  />
                </th>
                <th>COMPANY</th><th>PROVINCE</th><th>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {visibleCustomers.map((c) => {
                const checked = editing.customer_ids?.includes(c.customer_id);
                return (
                  <tr key={c.customer_id}
                    onClick={() => toggleCustomer(c.customer_id)}
                    style={{ cursor: "pointer" }}
                    className={checked ? "bg-amber-50" : ""}
                    data-testid={`group-customer-row-${c.customer_id}`}
                  >
                    <td><input type="checkbox" readOnly checked={!!checked} /></td>
                    <td className="text-sm">{c.company_name}</td>
                    <td className="font-mono text-xs">{c.province}</td>
                    <td className="font-mono text-xs">{c.status}</td>
                  </tr>
                );
              })}
              {visibleCustomers.length === 0 && (
                <tr><td colSpan={4} className="text-center text-zinc-400 py-6">No matches.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </SectionPad>
  );
}
